/**
 * Coalesces rapid chart re-renders into a single `requestAnimationFrame` tick.
 *
 * Each chart component's constructor `effect` calls `schedule(render)` when its
 * `chartOptions()` / `chartData()` signals change. If a WebSocket burst updates a
 * card's `cardDataSignal` several times in quick succession, the effect fires
 * multiple times within the same frame. Without coalescing, each firing destroys
 * and recreates the Highcharts instance — a major frontend multiplier (JEP-10271).
 *
 * Only the latest `render` callback runs, on the next animation frame. Earlier
 * pending renders are discarded. `cancel()` clears any pending render (call from
 * `ngOnDestroy`).
 */
export class ChartRenderCoordinator {
	private rafId: number | null = null;
	private pendingRender: (() => void) | null = null;

	schedule(render: () => void): void {
		this.pendingRender = render;
		if (this.rafId !== null) {
			return;
		}
		this.rafId = requestAnimationFrame(() => {
			this.rafId = null;
			const render = this.pendingRender;
			this.pendingRender = null;
			render?.();
		});
	}

	cancel(): void {
		if (this.rafId !== null) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}
		this.pendingRender = null;
	}
}
